import { datadogRum } from "@datadog/browser-rum"
import { browserIntakeUrl, DATADOG_SITE } from "./browser-intake"
import { scrubTelemetryText, telemetryError } from "./telemetry-safety"
import type { TelemetryWorkspace } from "./telemetry"

let started = false
let activeWorkspace: TelemetryWorkspace | undefined
let currentUserId: string | undefined
let workspaceRevision = 0
export function hasDatadogBrowserConfig() {
  return Boolean(process.env.NEXT_PUBLIC_DD_APPLICATION_ID && process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN)
}
export function initDatadogBrowser() {
  if (started || typeof window === "undefined" || !hasDatadogBrowserConfig()) return
  installReplayUrlGuard()
  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DD_APPLICATION_ID!, clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN!,
    site: DATADOG_SITE, proxy: browserIntakeUrl, service: "ortelian-platform",
    env: process.env.NEXT_PUBLIC_DD_ENV, version: process.env.NEXT_PUBLIC_DD_VERSION,
    sessionSampleRate: 100, sessionReplaySampleRate: 100, defaultPrivacyLevel: "mask",
    startSessionReplayRecordingManually: true, enablePrivacyForActionName: true,
    trackUserInteractions: true, trackResources: true, trackLongTasks: true,
    allowedTracingUrls: [{ match: (url: string) => { try { return new URL(url, location.href).origin === location.origin } catch { return false } }, propagatorTypes: ["tracecontext"] }],
    beforeSend: (event) => {
      event.view.url = scrubTelemetryText(event.view.url)
      if (event.view.referrer) event.view.referrer = scrubTelemetryText(event.view.referrer)
      if (event.type === "resource") event.resource.url = scrubTelemetryText(event.resource.url)
      if (event.type === "error") {
        // Browsers report a ResizeObserver loop as an uncaught error; it skips a frame of layout and loses no work.
        if (event.error.message.includes("ResizeObserver loop")) return false
        event.error.message = scrubTelemetryText(event.error.message)
        if (event.error.stack) event.error.stack = scrubTelemetryText(event.error.stack)
      }
      return true
    },
  })
  started = true
  updateReplay()
}
export function identifyUser(id: string) {
  initDatadogBrowser(); if (!started) return false
  if (currentUserId && currentUserId !== id) resetUser()
  currentUserId = id; datadogRum.setUser({ id }); publishIdentity(); return true
}
export function resetUser() {
  if (!started) return
  currentUserId = undefined; datadogRum.clearUser(); clearWorkspace(); datadogRum.stopSession()
}
export function bindWorkspace(workspace: TelemetryWorkspace) {
  initDatadogBrowser(); if (!started) return () => {}
  clearWorkspace()
  const revision = ++workspaceRevision
  activeWorkspace = workspace
  if (workspace.customerAccount) datadogRum.setAccount(workspace.customerAccount)
  publishIdentity()
  return () => { if (revision === workspaceRevision) clearWorkspace() }
}
export function clearWorkspace() {
  if (!started) return
  workspaceRevision++; activeWorkspace = undefined
  datadogRum.clearAccount(); publishIdentity()
}
function publishIdentity() {
  if (!currentUserId && !activeWorkspace) { datadogRum.removeGlobalContextProperty("ortelian"); return }
  datadogRum.setGlobalContextProperty("ortelian", {
    ...(currentUserId ? { user: { id: currentUserId } } : {}),
    ...(activeWorkspace ? { workspace: { id: activeWorkspace.id, slug: activeWorkspace.slug, name: activeWorkspace.name }, ...(activeWorkspace.customerAccount ? { company: activeWorkspace.customerAccount } : {}) } : {}),
  })
}
export function captureException(error: unknown, context?: Record<string, unknown>) { initDatadogBrowser(); if (started) datadogRum.addError(telemetryError(error), context) }

// Native replay snapshots include the document URL outside beforeSend. Stop
// recording before navigation and resume only on URLs without query/fragment
// data. RUM remains available with scrubbed URLs on every page.
function updateReplay() {
  if (!started) return
  if (location.search || location.hash) datadogRum.stopSessionReplayRecording()
  else datadogRum.startSessionReplayRecording()
}
function installReplayUrlGuard() {
  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method]
    history[method] = function (...args: Parameters<History[typeof method]>) {
      datadogRum.stopSessionReplayRecording()
      const result = original.apply(this, args)
      queueMicrotask(updateReplay)
      return result
    }
  }
  const navigation = () => { datadogRum.stopSessionReplayRecording(); queueMicrotask(updateReplay) }
  addEventListener("popstate", navigation)
  addEventListener("hashchange", navigation)
}
